import {
  ActivitySortOrder,
  CallPlanInfoFragment,
  type GetActivitiesQuery,
  type GetCallPlanActivitiesQuery,
  type PlaylistDetailedFragment,
} from "@/graphql/graphql";
import {
  accountCalendarDateToEndUtc,
  accountCalendarDateToStartUtc,
  addCalendarDays,
  formatAccountCalendarDateToday,
  instantToAccountCalendarDate,
} from "@/utils/date-utils";

import { duplicateProgramWeekEarlierPlanIds } from "../CallPlans.columns";

type PlanActivityRow = GetActivitiesQuery["activities"]["activities"][number];
type CallPlanInteractions = GetCallPlanActivitiesQuery["callPlan"]["interactions"];
export type Program = CallPlanInfoFragment["program"];
export type CallPlanActivityInteraction = Extract<
  NonNullable<NonNullable<CallPlanInteractions>[number]>,
  { __typename?: "ActivityInteraction" }
>;

export interface CallPlanFormValues {
  startDate: string;
  endDate: string;
}

/** Days from start to end for new/edit defaults (one week). */
export const CALL_PLAN_DURATION_DAYS = 7;

export const getCallPlanSubmitButtonLabel = ({
  createLoading,
  updateLoading,
  callPlanToEdit,
}: {
  createLoading: boolean;
  updateLoading: boolean;
  callPlanToEdit?: CallPlanInfoFragment | null;
}): string => {
  if (createLoading) {
    return "Creating...";
  }
  if (updateLoading) {
    return "Saving...";
  }
  if (callPlanToEdit) {
    return "Save Changes";
  }

  return "Create Call Plan";
};

export const sortOrderToMui = (order?: ActivitySortOrder | null): "asc" | "desc" =>
  order === ActivitySortOrder.Asc ? "asc" : "desc";

export const MAX_CALL_PLAN_ACTIVITIES = 7;

/** Maps call-plan interaction steps to the same row shape as GetActivities (picker table / selected list). */
export const callPlanInteractionsToPlanActivityRows = (
  interactions?: CallPlanInteractions | null
): PlanActivityRow[] => {
  if (!interactions?.length) {
    return [];
  }

  return interactions
    .filter(
      (i): i is CallPlanActivityInteraction =>
        i.__typename === "ActivityInteraction" && !!i.activityId
    )
    .sort((a, b) => a.order - b.order)
    .map(
      ({ activityId, title, category, difficulty, activityTypes }): PlanActivityRow => ({
        id: activityId as number,
        title,
        category,
        difficulty,
        activityTypes,
      })
    );
};

export const accountHasActiveCallPlan = (callPlans: CallPlanInfoFragment[]): boolean =>
  callPlans.some((plan) => plan.isActive);

/** End calendar day in account TZ equals today in account TZ. */
export const isCallPlanEndDateTodayInAccountTz = (
  callPlan: CallPlanInfoFragment,
  accountTimeZone?: string | null
): boolean => {
  if (!callPlan.endDate) {
    return false;
  }

  return (
    instantToAccountCalendarDate(callPlan.endDate, accountTimeZone) ===
    formatAccountCalendarDateToday(accountTimeZone)
  );
};

export const canReactivateCallPlan = (
  callPlan: CallPlanInfoFragment,
  callPlans: CallPlanInfoFragment[],
  accountTimeZone?: string | null
): boolean =>
  !accountHasActiveCallPlan(callPlans) &&
  !callPlan.isActive &&
  isCallPlanEndDateTodayInAccountTz(callPlan, accountTimeZone);

export const getCallPlanReactivateCandidate = (
  segmentPlans: CallPlanInfoFragment[],
  callPlans: CallPlanInfoFragment[],
  accountTimeZone?: string | null
): CallPlanInfoFragment | undefined =>
  segmentPlans.find((plan) => canReactivateCallPlan(plan, callPlans, accountTimeZone));

export const planTableToggleAriaLabel = (
  title: string,
  mode: "add" | "remove" | "blocked"
): string => {
  switch (mode) {
    case "remove":
      return `Remove ${title} from call plan`;
    case "blocked":
      return "Call plan is full; remove an activity to add another";
    case "add":
      return `Add ${title} to call plan`;
    default:
      return "";
  }
};

export const CREATE_CALL_PLAN_DISABLED_PROGRAM_ENDED_MESSAGE =
  "The program has ended. You cannot create a new call plan.";

export const CALL_PLAN_START_DATE_DELIVERIES_TOOLTIP =
  "Cannot edit start date of active call plans";

export const CALL_PLAN_CALL_DELIVERY_SPEED_TOOLTIP =
  "Call speed can be changed in the patient info tab";

/** Active plans or plans with deliveries keep the original start date in the edit modal. */
export const isCallPlanStartDateLocked = (callPlan?: CallPlanInfoFragment | null): boolean =>
  Boolean(callPlan?.isActive || callPlan?.hasDeliveries);

// The way a programHistory is set to active is VERY BAD. Using date ranges as a backup.
export const isActiveProgram = ({
  program_type,
  status,
  start_date,
  end_date,
}: {
  program_type?: string | null;
  status?: string | null;
  start_date: string;
  end_date?: string | null;
}): boolean => {
  if (program_type?.toLowerCase() !== "intensive") {
    return false;
  }
  if (status?.toLowerCase() === "active") {
    return true;
  }

  const today = new Date().toISOString();

  return start_date <= today && (!end_date || today <= end_date);
};

const callPlansForActiveProgram = (
  callPlans: CallPlanInfoFragment[],
  activeProgram?: Program | null
): CallPlanInfoFragment[] => {
  if (!activeProgram) {
    return [];
  }

  return callPlans.filter((cp) => cp.program.id === activeProgram.id);
};

const PROGRAM_WEEK_FROM_PLAYLIST_TITLE_REGEX = /[Ww]eek\s*(\d+)/;

/** Parses "week N" from a playlist title; returns null when the title does not match. */
export const parseProgramWeekFromPlaylistTitle = (title: string): number | null => {
  const match = title.match(PROGRAM_WEEK_FROM_PLAYLIST_TITLE_REGEX);
  if (!match) {
    return null;
  }

  const week = Number.parseInt(match[1], 10);
  return Number.isFinite(week) && week > 0 ? week : null;
};

const playlistBelongsToActiveProgram = (
  playlist: PlaylistDetailedFragment,
  activeProgram: Program
): boolean => {
  const { program } = playlist;
  if (!program) {
    return false;
  }

  return program.id === activeProgram.id && program.round === activeProgram.round;
};

const playlistsForActiveProgram = (
  playlists: PlaylistDetailedFragment[],
  activeProgram?: Program | null
): PlaylistDetailedFragment[] => {
  if (!activeProgram) {
    return [];
  }

  return playlists.filter((playlist) => playlistBelongsToActiveProgram(playlist, activeProgram));
};

/** Most recent playlist for the active program (by createdOn, then endOn). */
export const getMostRecentPlaylistForProgram = (
  playlists: PlaylistDetailedFragment[],
  activeProgram?: Program | null
): PlaylistDetailedFragment | null => {
  const forProgram = playlistsForActiveProgram(playlists, activeProgram);
  if (forProgram.length === 0) {
    return null;
  }

  return forProgram.reduce((latest, playlist) => {
    const latestCreated = new Date(latest.createdOn).getTime();
    const playlistCreated = new Date(playlist.createdOn).getTime();
    if (playlistCreated > latestCreated) {
      return playlist;
    }
    if (playlistCreated < latestCreated) {
      return latest;
    }

    const latestEnd = new Date(latest.endOn).getTime();
    const playlistEnd = new Date(playlist.endOn).getTime();
    return playlistEnd > latestEnd ? playlist : latest;
  });
};

const programWeekFromLatestPlaylist = (
  playlists: PlaylistDetailedFragment[],
  activeProgram?: Program | null
): number | null => {
  const latest = getMostRecentPlaylistForProgram(playlists, activeProgram);
  if (!latest?.title) {
    return null;
  }

  const latestPlaylistWeek = parseProgramWeekFromPlaylistTitle(latest.title);
  return latestPlaylistWeek != null ? latestPlaylistWeek + 1 : null;
};

/**
 * Week number for a new call plan on the active program: max existing week + 1, or latest matching
 * playlist week + 1 when there are no call plans yet (aligned with CreatePlaylistModal).
 */
export const getNextProgramWeekDisplay = (
  callPlans: CallPlanInfoFragment[],
  activeProgram?: Program | null,
  playlists: PlaylistDetailedFragment[] = []
): number => {
  const relevant = callPlansForActiveProgram(callPlans, activeProgram);
  if (relevant.length === 0) {
    return programWeekFromLatestPlaylist(playlists, activeProgram) ?? 1;
  }

  return Math.max(...relevant.map((cp) => cp.programWeek)) + 1;
};

export const computeEditFormValues = (
  callPlan: CallPlanInfoFragment,
  accountTimeZone?: string | null
): CallPlanFormValues => {
  const startDate = instantToAccountCalendarDate(callPlan.startDate, accountTimeZone);
  return {
    startDate,
    endDate:
      instantToAccountCalendarDate(callPlan.endDate ?? "", accountTimeZone) ||
      addCalendarDays(startDate, CALL_PLAN_DURATION_DAYS),
  };
};

export const computeDefaults = (
  callPlans: CallPlanInfoFragment[],
  activeProgram?: Program | null,
  accountTimeZone?: string | null
): CallPlanFormValues => {
  const relevant = callPlansForActiveProgram(callPlans, activeProgram);
  const today = formatAccountCalendarDateToday(accountTimeZone);
  if (relevant.length === 0) {
    return {
      startDate: today,
      endDate: addCalendarDays(today, CALL_PLAN_DURATION_DAYS),
    };
  }

  const latestEndDate = relevant.reduce<string | null>((latest, cp) => {
    if (!cp.endDate) {
      return latest;
    }
    const endCal = instantToAccountCalendarDate(cp.endDate, accountTimeZone);
    if (!latest || endCal > latest) {
      return endCal;
    }

    return latest;
  }, null);
  const dayAfterLatest = latestEndDate && addCalendarDays(latestEndDate, 1);
  const startDate = dayAfterLatest && dayAfterLatest > today ? dayAfterLatest : today;

  return {
    startDate,
    endDate: addCalendarDays(startDate, CALL_PLAN_DURATION_DAYS),
  };
};

/** Plans to compare for date overlap (latest segment per week only; excludes plan being edited). */
export const callPlansForOverlapComparison = (
  callPlans: CallPlanInfoFragment[],
  excludeCallPlanId?: number
): CallPlanInfoFragment[] => {
  const collapsedEarlierIds = new Set(duplicateProgramWeekEarlierPlanIds(callPlans));

  return callPlans.filter(
    (callPlan) => callPlan.id !== excludeCallPlanId && !collapsedEarlierIds.has(callPlan.id)
  );
};

/** Inclusive range overlap in UTC (matches API start/end instants). */
const rangesOverlap = (aStartIso: string, aEndIso: string, bStartIso: string, bEndIso: string) =>
  new Date(aStartIso).getTime() <= new Date(bEndIso).getTime() &&
  new Date(bStartIso).getTime() <= new Date(aEndIso).getTime();

const getCallPlanRangeOverlapError = (
  startDate: string,
  endDate: string,
  callPlans: CallPlanInfoFragment[],
  proposalStartIso?: string | null,
  accountTimeZone?: string | null
): string | false => {
  const proposalStart =
    proposalStartIso ?? accountCalendarDateToStartUtc(startDate, accountTimeZone);
  const proposalEnd = accountCalendarDateToEndUtc(endDate, accountTimeZone);

  const overlapping = callPlans.find((callPlan) => {
    if (!callPlan.endDate) {
      return true;
    }

    return rangesOverlap(proposalStart, proposalEnd, callPlan.startDate, callPlan.endDate);
  });

  return overlapping ? `Overlaps with week ${overlapping.programWeek}.` : false;
};

export const validateStartDate = (
  startDate: string,
  endDate: string,
  callPlans: CallPlanInfoFragment[],
  activeProgram?: Program | null,
  /** When true, start date is read-only in the UI (active plan or deliveries). */
  skipStartDateValidation = false,
  accountTimeZone?: string | null
): string | true => {
  if (skipStartDateValidation) {
    return true;
  }
  if (!startDate) {
    return "Start date is required.";
  }

  const today = formatAccountCalendarDateToday(accountTimeZone);
  if (startDate < today) {
    return "Start date cannot be in the past.";
  }
  if (!endDate) {
    return true;
  }
  if (startDate > endDate) {
    return "Must be before end date.";
  }
  if (activeProgram?.start_date && startDate < activeProgram.start_date.slice(0, 10)) {
    return "Start date cannot be before program start date.";
  }
  if (activeProgram?.end_date && startDate > activeProgram.end_date.slice(0, 10)) {
    return "Start date cannot be after program end date.";
  }

  const overlapError = getCallPlanRangeOverlapError(
    startDate,
    endDate,
    callPlans,
    undefined,
    accountTimeZone
  );
  if (overlapError) {
    return overlapError;
  }

  return true;
};

export const validateEndDate = (
  endDate: string,
  startDate: string,
  callPlans: CallPlanInfoFragment[],
  activeProgram?: Program | null,
  proposalStartIso?: string | null,
  accountTimeZone?: string | null
): string | true => {
  if (!endDate) {
    return "End date is required.";
  }
  if (startDate && endDate < startDate) {
    return "Must be after start date.";
  }
  if (activeProgram?.end_date && endDate > activeProgram.end_date.slice(0, 10)) {
    return "End date cannot be after program end date.";
  }

  if (startDate && endDate) {
    const overlapError = getCallPlanRangeOverlapError(
      startDate,
      endDate,
      callPlans,
      proposalStartIso,
      accountTimeZone
    );
    if (overlapError) {
      return overlapError;
    }
  }

  return true;
};
